import React, { memo, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { Paper, Typography, Box, alpha, Chip } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const ErrorHandlerNode = memo(({ data, selected, id }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Red theme colors
  const primaryColor = theme.palette.error.main; // Red
  const secondaryColor = theme.palette.error.dark; // Dark red
  const warningColor = theme.palette.warning.main; // Orange for warnings
  const textColor = theme.palette.common.white;

  const handleDoubleClick = useCallback((event) => {
    event.stopPropagation();
    const editEvent = new CustomEvent('editNode', { detail: { nodeId: id } });
    window.dispatchEvent(editEvent);
  }, [id]);

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const contextEvent = new CustomEvent('nodeContextMenu', {
      detail: {
        nodeId: id,
        x: event.clientX,
        y: event.clientY
      }
    });
    window.dispatchEvent(contextEvent);
  }, [id]);

  // Get error type color
  const getErrorTypeColor = (errorType) => {
    switch(errorType) {
      case 'critical':
        return theme.palette.error.dark;
      case 'warning':
        return warningColor;
      case 'retry':
        return theme.palette.info.main;
      default:
        return primaryColor;
    }
  };

  // Format retry policy display
  const formatRetryPolicy = (policy) => {
    if (!policy) return 'No retry';
    if (typeof policy === 'object') {
      return `${policy.attempts || 3}x (${policy.delay || '1s'})`;
    }
    return policy;
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Paper
        elevation={selected ? 12 : 6}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
        sx={{
          width: 140,
          height: 140,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          cursor: 'move',
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          // Octagon shape using clip-path
          clipPath: 'polygon(25% 0%, 75% 0%, 100% 25%, 100% 75%, 75% 100%, 25% 100%, 0% 75%, 0% 25%)',
          border: selected
            ? `3px solid ${theme.palette.secondary.main}`
            : `2px solid ${alpha(primaryColor, 0.3)}`,
          transition: theme.transitions.create([
            'transform', 'box-shadow', 'border', 'filter'
          ], {
            duration: theme.transitions.duration.shorter,
          }),
          '&:hover': {
            transform: 'scale(1.08) rotate(-2deg)',
            boxShadow: `0 12px 40px ${alpha(primaryColor, 0.4)}`,
            border: `3px solid ${alpha(theme.palette.secondary.main, 0.7)}`,
            filter: 'brightness(1.1)',
            '& .error-icon': {
              transform: 'scale(1.2) rotate(15deg)',
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.4))',
            },
            '& .warning-pulse': {
              animation: 'warning-pulse 1s ease-in-out infinite',
            }
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -6,
            left: -6,
            right: -6,
            bottom: -6,
            clipPath: 'inherit',
            background: `conic-gradient(from 45deg, ${primaryColor}, ${warningColor}, ${primaryColor})`,
            opacity: selected ? 0.6 : 0,
            transition: 'opacity 0.3s ease',
            zIndex: -1,
          },
          '@keyframes warning-pulse': {
            '0%, 100%': {
              opacity: 1,
            },
            '50%': {
              opacity: 0.7,
            },
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Error handler node: ${data.label || 'Error Handler'}`}
        aria-selected={selected}
      >
        {/* Input Handle */}
        <Handle
          type="target"
          position={Position.Top}
          style={{
            background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
            border: `2px solid ${theme.palette.background.paper}`,
            width: 14,
            height: 14,
            boxShadow: `0 2px 8px ${alpha(primaryColor, 0.3)}`,
            top: -7,
          }}
        />

        {/* Warning pulse effect */}
        <Box
          className="warning-pulse"
          sx={{
            position: 'absolute',
            top: -12,
            left: -12,
            right: -12,
            bottom: -12,
            clipPath: 'inherit',
            border: `3px solid ${alpha(warningColor, 0.6)}`,
            opacity: 0,
          }}
        />

        {/* Main content */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          zIndex: 1,
        }}>
          <ErrorOutline
            className="error-icon"
            sx={{
              fontSize: 36,
              color: textColor,
              mb: 0.5,
              filter: `drop-shadow(0 2px 4px ${alpha(theme.palette.common.black, 0.3)})`,
              transition: 'all 0.3s ease',
            }}
          />
          <Typography
            variant="caption"
            fontWeight="700"
            sx={{
              color: textColor,
              fontSize: '0.75rem',
              lineHeight: 1.1,
              textShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.3)}`,
              maxWidth: '90px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {data.label || 'Error Handler'}
          </Typography>
        </Box>

        {/* Error type indicator */}
        {data.errorType && (
          <Box
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${getErrorTypeColor(data.errorType)}, ${alpha(getErrorTypeColor(data.errorType), 0.8)})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.common.white,
              fontSize: '0.7rem',
              fontWeight: 'bold',
              border: `2px solid ${theme.palette.background.paper}`,
              boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
              textTransform: 'uppercase',
            }}
          >
            {data.errorType === 'critical' ? '!' :
             data.errorType === 'warning' ? '⚠' :
             data.errorType === 'retry' ? '↻' : '⚡'}
          </Box>
        )}

        {/* Success/Recovery Handle */}
        <Handle
          type="source"
          position={Position.Right}
          id="success"
          style={{
            background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
            border: `2px solid ${theme.palette.background.paper}`,
            width: 14,
            height: 14,
            right: -7,
            boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.3)}`,
          }}
        />

        {/* Failure Handle */}
        <Handle
          type="source"
          position={Position.Bottom}
          id="failure"
          style={{
            background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
            border: `2px solid ${theme.palette.background.paper}`,
            width: 14,
            height: 14,
            bottom: -7,
            boxShadow: `0 2px 8px ${alpha(primaryColor, 0.3)}`,
          }}
        />
      </Paper>

      {/* Error type label */}
      {data.errorType && (
        <Chip
          label={data.errorType.toUpperCase()}
          size="small"
          sx={{
            position: 'absolute',
            top: -25,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: getErrorTypeColor(data.errorType),
            color: textColor,
            fontSize: '0.65rem',
            height: 20,
            fontWeight: 700,
            border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
            backdropFilter: 'blur(4px)',
          }}
        />
      )}

      {/* Retry policy display */}
      {data.retryPolicy && (
        <Chip
          label={formatRetryPolicy(data.retryPolicy)}
          size="small"
          sx={{
            position: 'absolute',
            bottom: -25,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: alpha(theme.palette.info.main, 0.9),
            color: textColor,
            fontSize: '0.65rem',
            height: 20,
            maxWidth: 120,
            fontWeight: 600,
            border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
            backdropFilter: 'blur(4px)',
          }}
        />
      )}

      {/* Success/Failure Labels */}
      <Chip
        label="RECOVER"
        size="small"
        sx={{
          position: 'absolute',
          top: '50%',
          right: -35,
          transform: 'translateY(-50%)',
          backgroundColor: theme.palette.success.main,
          color: theme.palette.common.white,
          fontSize: '0.6rem',
          height: 18,
          minWidth: 55,
          fontWeight: 700,
          opacity: selected ? 1 : 0.8,
          transition: theme.transitions.create('opacity'),
        }}
      />

      <Chip
        label="FAIL"
        size="small"
        sx={{
          position: 'absolute',
          bottom: -35,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: primaryColor,
          color: textColor,
          fontSize: '0.6rem',
          height: 18,
          minWidth: 40,
          fontWeight: 700,
          opacity: selected ? 1 : 0.8,
          transition: theme.transitions.create('opacity'),
        }}
      />
    </Box>
  );
});

ErrorHandlerNode.displayName = 'ErrorHandlerNode';

export default ErrorHandlerNode;